import React, { useState, useEffect } from "react"
import DOMPurify from "dompurify"
import PropTypes from "prop-types"
import { Flex, Switch, Tag, Tooltip, Spin, Modal } from "antd"
import { EyeOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import DSTab from "ui/DSTab"
import { Text } from "ui/Typography"
import styles from "./index.module.css"
import EditTemplateModal from "./EditTemplateModal"
import {
  fetchCommunicationMessages,
  updateManualMessage,
  updateTemplateContent,
  fetchTemplateContent,
  marketplaceMessagesSelector,
  manualMessagesSelector,
  communicationLoadingSelector,
  communicationUpdatingIdSelector,
  communicationUpdatingContentIdSelector,
  communicationFetchingContentIdSelector,
  communicationContentByIdSelector
} from "store/communication"

const TAB_KEYS = { MARKETPLACE: "marketplace", MANUAL: "manual" }
const FILTER_KEYS = { ALL: "all", EMAIL: "email", SMS: "sms" }

const FilterBar = ({ filter, setFilter, extra }) => {
  const { t } = useTranslation()
  const filterLabels = {
    [FILTER_KEYS.ALL]: t("communication_module.filter.all"),
    [FILTER_KEYS.EMAIL]: t("communication_module.filter.email"),
    [FILTER_KEYS.SMS]: t("communication_module.filter.sms")
  }
  return (
    <Flex align="center" justify="space-between" className={styles.filterRow}>
      <Flex gap={8}>
        {[FILTER_KEYS.ALL, FILTER_KEYS.EMAIL, FILTER_KEYS.SMS].map((key) => (
          <button
            key={key}
            className={`${styles.filterBtn} ${filter === key ? styles.filterBtnActive : ""}`}
            onClick={() => setFilter(key)}
          >
            {filterLabels[key]}
          </button>
        ))}
      </Flex>
      {extra}
    </Flex>
  )
}

FilterBar.propTypes = {
  filter: PropTypes.string.isRequired,
  setFilter: PropTypes.func.isRequired,
  extra: PropTypes.node
}

const CommunicationList = ({ items, withToggle, withDisabled, onToggle, updatingId, withEdit, onFetchContent, onSaveContent, updatingContentId, fetchingContentId, contentById }) => {
  const { t } = useTranslation()
  const [previewItem, setPreviewItem] = useState(null)
  const [editItem, setEditItem] = useState(null)
  const [savingId, setSavingId] = useState(null)

  useEffect(() => {
    if (savingId && updatingContentId !== savingId) {
      setEditItem(null)
      setSavingId(null)
    }
  }, [updatingContentId, savingId])

  const handleEditClick = (item) => {
    setEditItem(item)
    onFetchContent?.(item.id)
  }

  const sections = [...new Set(items.map((i) => i.section))]
  return (
    <>
      <div>
        {sections.map((section) => (
          <div key={section}>
            <Text className={styles.sectionHeader}>{section}</Text>
            <div className={styles.sectionContent}>
              {items
                .filter((item) => item.section === section)
                .map((item, idx) => (
                  <div
                    key={`${section}-${item.type}-${item.name}-${idx}`}
                    className={`${styles.row} ${withDisabled && item.disabled ? styles.rowDisabled : ""}`}
                  >
                    <Tag className={`${styles.tag} ${item.type === "sms" ? styles.tagSms : styles.tagEmail}`}>
                      {item.type.toUpperCase()}
                    </Tag>
                    <div className={styles.rowMeta}>
                      <Text className={styles.rowName}>
                        {item.name}
                        {item.type === "sms" && (
                          <Tooltip title={t("communication_module.sms_tooltip")}>
                            <InfoCircleOutlined className={styles.smsInfoIcon} />
                          </Tooltip>
                        )}
                      </Text>
                      {item.trigger_event && <Text className={`${styles.rowTiming} ${styles.rowTimingMobile}`}>{item.trigger_event}</Text>}
                    </div>
                    {item.trigger_event && <Text className={`${styles.rowTiming} ${styles.rowTimingDesktop}`}>{item.trigger_event}</Text>}
                    <div className={styles.rowActions}>
                      {withToggle && (
                        <Switch
                          size="small"
                          checked={item.enabled}
                          disabled={updatingId === item.id}
                          onChange={(checked) => onToggle?.(item, checked)}
                        />
                      )}
                      <EyeOutlined className={styles.eyeIcon} onClick={() => setPreviewItem(item)} />
                      {withEdit && (
                        item.type === "email"
                          ? <EditOutlined
                              className={`${styles.editIcon} ${item.is_editable ? "" : styles.editIconDisabled}`}
                              onClick={() => item.is_editable && handleEditClick(item)}
                            />
                          : <span className={styles.editIconPlaceholder} />
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
      <Modal
        open={!!previewItem}
        onCancel={() => setPreviewItem(null)}
        footer={null}
        title={previewItem?.subject || previewItem?.name}
        width="min(840px, 95vw)"
      >
        {previewItem && (
          <div
            className={styles.emailPreview}
            dangerouslySetInnerHTML={{
              __html: (() => {
                DOMPurify.addHook("afterSanitizeAttributes", (node) => {
                  if (node.tagName === "A") {
                    node.removeAttribute("href")
                    node.removeAttribute("target")
                  }
                })
                const clean = DOMPurify.sanitize(previewItem.body, {
                  ADD_ATTR: ["style", "width", "height", "align", "valign", "bgcolor", "cellpadding", "cellspacing", "border", "target"],
                  FORCE_BODY: true
                })
                DOMPurify.removeHooks("afterSanitizeAttributes")
                return clean
              })()
            }}
          />
        )}
      </Modal>
      <EditTemplateModal
        item={editItem}
        content={editItem ? contentById?.[editItem.id] : null}
        open={!!editItem}
        onClose={() => setEditItem(null)}
        onSave={(id, messageBody, footer) => {
          setSavingId(id)
          onSaveContent?.(id, messageBody, footer)
        }}
        loading={fetchingContentId === editItem?.id}
        saving={updatingContentId === editItem?.id}
      />
    </>
  )
}

CommunicationList.propTypes = {
  items: PropTypes.array.isRequired,
  withToggle: PropTypes.bool,
  withDisabled: PropTypes.bool,
  onToggle: PropTypes.func,
  updatingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  withEdit: PropTypes.bool,
  onFetchContent: PropTypes.func,
  onSaveContent: PropTypes.func,
  updatingContentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fetchingContentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contentById: PropTypes.object
}

const MarketplaceTab = () => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState(FILTER_KEYS.ALL)
  const dispatch = useDispatch()
  const items = useSelector(marketplaceMessagesSelector)
  const loading = useSelector(communicationLoadingSelector)

  useEffect(() => {
    dispatch(fetchCommunicationMessages())
  }, [dispatch])

  const filtered = items.filter((i) => filter === FILTER_KEYS.ALL || i.type === filter)

  return (
    <>
      <div className={styles.infoBox}>
        <InfoCircleOutlined className={styles.infoIcon} />
        <Text>{t("communication_module.marketplace_info")}</Text>
      </div>
      <FilterBar filter={filter} setFilter={setFilter} />
      {loading ? (
        <Flex justify="center" align="center" className={styles.spinContainer}>
          <Spin />
        </Flex>
      ) : (
        <CommunicationList items={filtered} withToggle={false} />
      )}
    </>
  )
}

const ManualTab = () => {
  const [filter, setFilter] = useState(FILTER_KEYS.ALL)
  const dispatch = useDispatch()
  const items = useSelector(manualMessagesSelector)
  const loading = useSelector(communicationLoadingSelector)
  const updatingId = useSelector(communicationUpdatingIdSelector)
  const updatingContentId = useSelector(communicationUpdatingContentIdSelector)
  const fetchingContentId = useSelector(communicationFetchingContentIdSelector)
  const contentById = useSelector(communicationContentByIdSelector)

  useEffect(() => {
    dispatch(fetchCommunicationMessages())
  }, [dispatch])

  const handleToggle = (targetItem, checked) => {
    dispatch(updateManualMessage(targetItem.id, checked))
  }

  const handleFetchContent = (id) => {
    dispatch(fetchTemplateContent(id))
  }

  const handleSaveContent = (id, messageBody, footer) => {
    dispatch(updateTemplateContent(id, messageBody, footer))
  }

  const filtered = items.filter((i) => filter === FILTER_KEYS.ALL || i.type === filter)

  if (loading) {
    return (
      <Flex justify="center" align="center" className={styles.spinContainer}>
        <Spin />
      </Flex>
    )
  }

  return (
    <>
      <FilterBar filter={filter} setFilter={setFilter} />
      <CommunicationList
        items={filtered}
        withToggle
        withDisabled
        onToggle={handleToggle}
        updatingId={updatingId}
        withEdit
        onFetchContent={handleFetchContent}
        onSaveContent={handleSaveContent}
        updatingContentId={updatingContentId}
        fetchingContentId={fetchingContentId}
        contentById={contentById}
      />
    </>
  )
}

export const CommunicationModule = () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(TAB_KEYS.MARKETPLACE)

  const tabItems = [
    { key: TAB_KEYS.MARKETPLACE, label: t("communication_module.tabs.marketplace") },
    { key: TAB_KEYS.MANUAL, label: t("communication_module.tabs.manual") }
  ]

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabsWrapper}>
        <DSTab items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
      </div>
      <div className={styles.content}>
        {activeTab === TAB_KEYS.MARKETPLACE && <MarketplaceTab />}
        {activeTab === TAB_KEYS.MANUAL && <ManualTab />}
      </div>
    </div>
  )
}
